import { type Check, TransactionsDetailModal } from '@/modules/projects'
import type { CheckSequenceGap } from '@/modules/projects/stores/types'
import { StatTileGrid, type StatTileProps } from '../shared'
import { formatMissingRanges } from './columns'

interface CheckGapDetailModalProps {
  gap: CheckSequenceGap | null
  checks: Check[]
  onOpenChange: (open: boolean) => void
}

export function CheckGapDetailModal({
  gap,
  checks,
  onOpenChange,
}: CheckGapDetailModalProps) {
  const title = gap
    ? `Account ${gap.accountNumber}${gap.accountOwner ? `, ${gap.accountOwner}` : ''}`
    : ''
  const description = gap
    ? `${gap.missingCount} missing check ${gap.missingCount === 1 ? 'number' : 'numbers'} in the issued range ${gap.from}-${gap.to}. Below are the ${checks.length} check${checks.length === 1 ? '' : 's'} on file for this account.`
    : ''

  return (
    <TransactionsDetailModal
      open={!!gap}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      // The checks slice is treated as transactions across the checks UI (see
      // the checks route), so the shared detail modal renders them directly.
      transactions={checks}
      contextId="checks-sequence-gaps"
      exportFilename="check-sequence-gaps"
      emptyMessage="No checks on file for this account."
      metadata={gap && <GapMetadata gap={gap} />}
    />
  )
}

const TILE_CLASS = 'px-4 py-2.5'
const LABEL_CLASS = 'text-blue-6'
const VALUE_CLASS = 'text-blue-7'

function GapMetadata({ gap }: { gap: CheckSequenceGap }) {
  const tiles: StatTileProps[] = [
    {
      label: 'Missing',
      value: gap.missingCount.toLocaleString(),
      sub: 'check numbers in the gap',
      valueClassName: 'text-red-600',
      labelClassName: LABEL_CLASS,
      className: TILE_CLASS,
    },
    {
      label: 'Present',
      value: gap.presentCount.toLocaleString(),
      sub: 'checks on file',
      valueClassName: VALUE_CLASS,
      labelClassName: LABEL_CLASS,
      className: TILE_CLASS,
    },
    {
      label: 'Issued range',
      value: `${gap.from}-${gap.to}`,
      sub: 'first to last number',
      valueClassName: VALUE_CLASS,
      labelClassName: LABEL_CLASS,
      className: TILE_CLASS,
    },
  ]

  return (
    <div className="overflow-hidden rounded-lg border">
      <StatTileGrid tiles={tiles} columns={3} />
      <div className="border-t bg-white px-4 py-3">
        <p className="text-blue-6 text-xs">Missing numbers</p>
        <p className="mt-1 text-blue-7 text-sm">{formatMissingRanges(gap)}</p>
      </div>
    </div>
  )
}
